import React, { useState, useEffect } from 'react';

// Funções utilitárias para cores
const hexToRgb = (hex: string): { r: number, g: number, b: number } => {
    hex = hex.replace('#', '');
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return { r, g, b };
};

const rgbToHex = ({ r, g, b }: { r: number, g: number, b: number }): string => {
    const toHex = (c: number) => {
        const hex = Math.max(0, Math.min(255, c)).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const rgbToHsv = ({ r, g, b }: { r: number, g: number, b: number }): { h: number, s: number, v: number } => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s, v = max;
    const d = max - min;
    s = max === 0 ? 0 : d / max;
    if (max !== min) {
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return { h: h * 360, s, v };
};

const hsvToRgb = ({ h, s, v }: { h: number, s: number, v: number }): { r: number, g: number, b: number } => {
    h /= 360;
    let r, g, b;
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);
    switch (i % 6) {
        case 0: r = v; g = t; b = p; break;
        case 1: r = q; g = v; b = p; break;
        case 2: r = p; g = v; b = t; break;
        case 3: r = p; g = q; b = v; break;
        case 4: r = t; g = p; b = v; break;
        case 5: r = v; g = p; b = q; break;
        default: r = 0; g = 0; b = 0;
    }
    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
    };
};

interface ColorPickerProps {
    color: string;
    onChange: (color: string) => void;
    label: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [rgbValues, setRgbValues] = useState({ r: 0, g: 0, b: 0 });
    const [hue, setHue] = useState(0);

    // Inicializar valores RGB a partir da cor hex quando o componente é montado ou a cor muda
    useEffect(() => {
        try {
            const rgb = hexToRgb(color);
            setRgbValues(rgb);
            const hsv = rgbToHsv(rgb);
            setHue(hsv.h);
        } catch (e) {
            console.error("Erro ao converter cor:", e);
        }
    }, [color]);


    const handleGradientClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

        const hsv = { h: hue, s: x, v: 1 - y };
        const rgb = hsvToRgb(hsv);

        setRgbValues(rgb);
        onChange(rgbToHex(rgb));
    };

    const handleColorSelectorDrag = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.buttons !== 1) return;
        handleGradientClick(e);
    };

    const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newHue = Number(e.target.value);
        setHue(newHue);

        const hsv = rgbToHsv(rgbValues);
        const newRgb = hsvToRgb({ h: newHue, s: hsv.s, v: hsv.v });

        setRgbValues(newRgb);
        onChange(rgbToHex(newRgb));
    };

    const handleRgbChange = (component: 'r' | 'g' | 'b') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        if (isNaN(value)) return;

        // Limitar valores entre 0 e 255
        const validValue = Math.max(0, Math.min(255, value));
        const newRgbValues = { ...rgbValues, [component]: validValue };
        setRgbValues(newRgbValues);

        // Atualizar cor hexadecimal
        const newColor = rgbToHex(newRgbValues);
        onChange(newColor);

        // Atualizar o matiz
        const hsv = rgbToHsv(newRgbValues);
        setHue(hsv.h);
    };

    return (
        <div className="relative">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
            <div
                className="w-full h-12 rounded-md border-2 border-blue-500 cursor-pointer hover:border-blue-600 transition-colors"
                style={{ backgroundColor: color }}
                onClick={() => setIsOpen(!isOpen)}
            />

            {isOpen && (
                <div className="absolute z-[60] mt-2 p-3 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 w-48 left-0">
                    {/* Gradiente de cor */}
                    <div
                        className="w-full h-20 rounded-md mb-3 relative cursor-crosshair"
                        style={{
                            background: `linear-gradient(to bottom, rgba(0,0,0,0) 0%, #000 100%),
                                        linear-gradient(to right, #fff 0%, hsl(${hue}, 100%, 50%) 100%)`
                        }}
                        onClick={handleGradientClick}
                        onMouseMove={handleColorSelectorDrag}
                    >
                        {/* Seletor de cor */}
                        <div
                            className="absolute w-4 h-4 rounded-full border-2 border-white shadow-md transform -translate-x-1/2 -translate-y-1/2"
                            style={{
                                backgroundColor: color,
                                top: `${100 - (rgbToHsv(rgbValues).v * 100)}%`,
                                left: `${rgbToHsv(rgbValues).s * 100}%`
                            }}
                        />
                    </div>

                    {/* Slider de matiz */}
                    <div className="mb-3">
                        <input
                            type="range"
                            min="0"
                            max="360"
                            value={hue}
                            onChange={handleHueChange}
                            className="w-full h-2 rounded-md appearance-none cursor-pointer"
                            style={{
                                background: `linear-gradient(to right, 
                                    #f00 0%, #ff0 17%, #0f0 33%, 
                                    #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)`
                            }}
                        />
                    </div>

                    {/* Input Hex e RGB */}
                    <div className="space-y-2 mb-3">
                        <div>
                            <input
                                type="text"
                                value={color}
                                onChange={(e) => onChange(e.target.value)}
                                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-center font-mono"
                                placeholder="#000000"
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-1">
                            <div>
                                <label className="text-[10px] text-gray-500 block text-center">R</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="255"
                                    value={rgbValues.r}
                                    onChange={handleRgbChange('r')}
                                    className="w-full px-1 py-1 text-[10px] border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-500 block text-center">G</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="255"
                                    value={rgbValues.g}
                                    onChange={handleRgbChange('g')}
                                    className="w-full px-1 py-1 text-[10px] border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-500 block text-center">B</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="255"
                                    value={rgbValues.b}
                                    onChange={handleRgbChange('b')}
                                    className="w-full px-1 py-1 text-[10px] border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between">
                        <button
                            onClick={() => {
                                onChange('#ffffff');
                                setIsOpen(false);
                            }}
                            className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                            Resetar
                        </button>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColorPicker;
